// src/utils/portalForms.js
// Pure schema for the client-portal forms (Process Street parity). No React,
// no IO. This is a registry of every form a portal deal can show.
//
// Field:  { key, label, type, group?, options?, placeholder?, required?, showIf?, addLabel? }
//   type ∈ "shortText" | "longText" | "single" | "multi" | "repeatable" | "file" | "video"
//   - shortText   one-line input          answer: string
//   - longText    textarea                answer: string
//   - single      choose one (options)    answer: string
//   - multi       choose many (options)   answer: string[]
//   - repeatable  "+ Add X" list          answer: string[] (each entry a line); carries addLabel
//   - file        upload                  answer: { name, driveFileId?, storagePath? } | null
//   - video       read-only clip          no answer; carries { key, type, label, url }
//   showIf?: { field, equals } — the field is only shown / required / counted when
//            the sibling answer equals the given value (see isFieldVisible).
// Form:   { id, title, intro, side: "seller" | "buyer" | "both", fields: [Field] }

// --- field builders ---------------------------------------------------------
// F keeps the exact shape shipped in Slice 1 (utility sheet); do not change it.
const F = (key, label, group, placeholder = "") => ({ key, label, type: "shortText", group, placeholder });

const short = (key, label, opts = {}) => ({ key, label, type: "shortText", ...opts });
const long = (key, label, opts = {}) => ({ key, label, type: "longText", ...opts });
const single = (key, label, options, opts = {}) => ({ key, label, type: "single", options, ...opts });
const multi = (key, label, options, opts = {}) => ({ key, label, type: "multi", options, ...opts });
const repeatable = (key, label, opts = {}) => ({ key, label, type: "repeatable", ...opts });
const file = (key, label, opts = {}) => ({ key, label, type: "file", ...opts });
const video = (key, title, url = "") => ({ key, type: "video", label: title, url });

const DEFAULT_GROUP = "";

// --- allAboutYou ------------------------------------------------------------
const ALL_ABOUT_YOU = {
  id: "allAboutYou",
  title: "All About You",
  intro:
    "As part of our ongoing effort to build clients for life, we'd like to learn a bit about you and your family.",
  side: "both",
  fields: [
    short("fullLegalName", "Full Legal Name", { group: "About you", required: true }),
    short("preferredName", "Preferred Name", { group: "About you" }),
    short("birthday", "Birthday", { group: "About you", placeholder: "MM/DD/YYYY", required: true }),
    short("employer", "Employer", { group: "About you" }),
    short("email", "Email", { group: "About you", placeholder: "you@example.com" }),
    short("homePhone", "Home Phone", { group: "About you" }),
    short("mobilePhone", "Mobile Phone", { group: "About you", required: true }),
    multi("bestContact", "Best Way to Reach You", ["Phone", "Email", "Text"], { group: "About you", required: true }),
    short("currentMailingAddress", "Current Mailing Address", { group: "About you" }),
    short("mostImportantQuality", "Most Important Quality You Want From Us?", { group: "About you", required: true }),
    short("howHeard", "How Did You Hear About Us?", { group: "About you" }),
    single("children", "Do You Have Children?", ["Yes", "No"], { group: "Family & pets" }),
    repeatable("childrenNames", "Children's Names", {
      group: "Family & pets",
      addLabel: "Add child",
      showIf: { field: "children", equals: "Yes" },
    }),
    single("hasPets", "Do You Have Pets?", ["Yes", "No"], { group: "Family & pets" }),
    multi("petType", "My/Our Pet(s) Is A…", ["Dog", "Cat", "Other"], {
      group: "Family & pets",
      showIf: { field: "hasPets", equals: "Yes" },
    }),
    repeatable("petNames", "Pet Names", {
      group: "Family & pets",
      addLabel: "Add pet",
      showIf: { field: "hasPets", equals: "Yes" },
    }),
    short("favRestaurant", "Favorite Restaurant", { group: "Favorites", required: true }),
    short("favCandy", "Favorite Candy", { group: "Favorites", required: true }),
    short("favNonAlc", "Favorite Non-Alcoholic Beverage", { group: "Favorites", required: true }),
    short("favAlc", "Favorite Alcoholic Beverage", { group: "Favorites" }),
  ],
};

// --- buyerPreferences -------------------------------------------------------
const BUYER_PREFERENCES = {
  id: "buyerPreferences",
  title: "Buyer Preferences",
  intro: "Tell us what's important to you in your new home!",
  side: "buyer",
  fields: [
    long("preferredCities", "Preferred Cities"),
    long("preferredNeighborhoods", "Preferred Neighborhoods"),
    short("priceRange", "Price Range"),
    short("propertyStyle", "Property Style / Type"),
    short("bedrooms", "Number of Bedrooms"),
    short("bathrooms", "Number of Bathrooms"),
    short("garage", "Garage Preferences"),
    short("sqftLot", "Square Footage / Lot Size"),
    short("pets", "Pets"),
    short("acOther", "Air Conditioning / Other"),
    long("additionalPreferences", "Additional Preferences"),
  ],
};

// --- preApproval ------------------------------------------------------------
const PRE_APPROVAL = {
  id: "preApproval",
  title: "Pre-Approval Letter",
  intro: "Please upload your pre-approval letter here.",
  side: "buyer",
  fields: [file("preApprovalFile", "Pre-Approval Letter")],
};

// --- utilityInfoSheet (SHIPPED — fields kept byte-for-byte) ------------------
export const UTILITY_SHEET = {
  id: "utilityInfoSheet",
  title: "Utility Information Sheet",
  intro:
    "A quick reference for the new owners: utilities, schools, appliance ages, and the little things that make the neighborhood home.",
  side: "seller",
  fields: [
    F("electricCompany","Electric Company","Utilities","e.g. ComEd"),
    F("electricPhone","Electric Phone","Utilities"),
    F("electricAvg","Electric Avg $/mo","Utilities"),
    F("gasCompany","Gas Company","Utilities","e.g. Nicor"),
    F("gasPhone","Gas Phone","Utilities"),
    F("gasAvg","Gas Avg $/mo","Utilities"),
    F("waterCompany","Water / Sewer Company","Utilities"),
    F("waterPhone","Water / Sewer Phone","Utilities"),
    F("waterAvg","Water / Sewer Avg $/mo","Utilities"),
    F("garbageCompany","Garbage / Recycling Company","Utilities"),
    F("garbagePhone","Garbage / Recycling Phone","Utilities"),
    F("garbageAvg","Garbage / Recycling Avg $/mo","Utilities"),
    F("cableCompany","Cable / Satellite / Internet Company","Utilities"),
    F("cablePhone","Cable / Satellite / Internet Phone","Utilities"),
    F("cableAvg","Cable / Satellite / Internet Avg $/mo","Utilities"),
    F("schoolDistrict","School District","Schools"),
    F("elementary","Elementary School","Schools"),
    F("middle","Middle School","Schools"),
    F("high","High School","Schools"),
    F("ageRoof","Roof","Ages","e.g. 6 yrs"),
    F("ageFurnace","Furnace / Boiler","Ages"),
    F("ageAC","AC","Ages"),
    F("ageWaterHeater","Water Heater","Ages"),
    F("ageWindows","Windows","Ages"),
    F("ageRefrigerator","Refrigerator","Ages"),
    F("ageStove","Stove / Oven","Ages"),
    F("ageDishwasher","Dishwasher","Ages"),
    F("ageWasherDryer","Washer / Dryer","Ages"),
    F("blockParties","Block Parties / Get-Togethers","Neighborhood"),
    F("halloween","Busy at Halloween?","Neighborhood"),
    F("eventsAware","Events to Be Aware Of","Neighborhood"),
  ],
};

// --- listOfUpdates ----------------------------------------------------------
const buildUpdateRows = () => {
  const rows = [];
  for (let i = 1; i <= 20; i++) {
    rows.push(short(`update${i}`, `Update ${i}`));
    rows.push(short(`updateYear${i}`, "Approx. Year"));
  }
  return rows;
};
const LIST_OF_UPDATES = {
  id: "listOfUpdates",
  title: "List of Updates in Your Home",
  intro: "This goes into marketing materials for your home. It saves as you go.",
  side: "seller",
  fields: [
    short("propertyAddress", "Property Address", { required: true }),
    ...buildUpdateRows(),
  ],
};

// --- homeQuestionnaire ------------------------------------------------------
const HOME_QUESTIONNAIRE = {
  id: "homeQuestionnaire",
  title: "Home Questionnaire",
  intro: "Tell us the details that make your home unique. It helps our marketing.",
  side: "seller",
  fields: [
    short("propertyAddress", "Property Address", { required: true }),
    multi("fencing", "Fencing (Check All That Apply)", [
      "None", "Invisible", "Wood", "Chain Link", "Rail",
      "Vinyl", "Composite", "Privacy", "Full", "Partial",
    ]),
    multi("countertops", "Countertops (Check All That Apply)", [
      "Granite", "Quartz", "Corian", "Formica", "Butcher Block",
      "Soapstone", "Concrete", "Laminate", "Other",
    ]),
    multi("flooring", "Flooring (Check All That Apply)", [
      "Hardwood", "Tile", "Laminate", "Stone", "EZFloor", "Other",
    ]),
    multi("airConditioning", "Air Conditioning", [
      "Central", "Window", "Wall", "Geothermal", "Ductless Mini-Split", "None", "Other",
    ]),
    multi("heating", "Heating", [
      "Forced Air", "Baseboard", "Hot Water", "Boiler", "Gravity", "Space Heater",
      "Heat Pump", "Steam", "In-Floor Heating", "Radiant", "Geothermal",
      "Ductless Mini-Split", "Other",
    ]),
    single("heatedFloors", "Any Heated Floors?", ["Yes", "No"]),
    short("heatedFloorRooms", "If Yes, Which Rooms?"),
    multi("fireplaceType", "Fireplace Type (If Any)", ["Wood burning", "Gas", "Electric"]),
    short("fireplacesWorking", "Are All Fireplaces in Working Order?"),
    long("hoa", "HOA? If Yes, Name / Contact / Dues / Amenities Covered"),
    short("uniqueStorage", "Any Unique Storage Space?"),
    short("uniqueFeatures", "Other Unique Features to Highlight?"),
  ],
};

// --- unitInfoQuestionnaire (condo variant of homeQuestionnaire) -------------
// Same fields/options as the Home Questionnaire, adapted for a unit. Distinct id.
const UNIT_INFO_QUESTIONNAIRE = {
  id: "unitInfoQuestionnaire",
  title: "Unit Information Questionnaire",
  intro: "Tell us the details that make your unit unique. It helps our marketing.",
  side: "seller",
  fields: HOME_QUESTIONNAIRE.fields,
};

// --- associationInfo (condo / townhome) ------------------------------------
const ASSOCIATION_INFO = {
  id: "associationInfo",
  title: "Association Information",
  intro: "A few details about your association so we can pass them along to the buyers.",
  side: "seller",
  fields: [
    short("propertyAddress", "Property Address", { required: true }),
    short("associationName", "Association Name", { required: true }),
    short("associationContact", "Association Contact Person"),
    short("associationContactInfo", "Association Contact's Email & Phone Number"),
    single("duesFrequency", "How Are Your Dues Paid?", ["Monthly", "Quarterly", "Annually"]),
    short("duesAmount", "What Is the Amount of Your Dues?"),
    single("specialAssessments", "Any Special Assessments?", ["Yes", "No"]),
    short("assessmentDetails", "If Yes, Please Provide the Assessment Amount & Details"),
  ],
};

// --- poolQuestionnaire ------------------------------------------------------
const POOL_QUESTIONNAIRE = {
  id: "poolQuestionnaire",
  title: "Pool Questionnaire",
  intro: "A few details about your pool so we can share them with the buyers.",
  side: "seller",
  fields: [
    short("propertyAddress", "Property Address", { required: true }),
    short("poolDimensions", "Pool Dimensions"),
    single("waterType", "Type of Water", ["Fresh water", "Salt water"]),
    single("heated", "Heated?", ["Yes", "No"]),
    short("coverType", "Type of Cover"),
    long("builderInfo", "Name of Builder/Installer & Contact Information"),
    long("maintenanceCompany", "Pool Maintenance Company & Contact Info"),
    long("maintenanceCost", "Cost of Maintenance"),
    short("systemAges", "Age / Replacement Date of the Pool Systems, If Applicable"),
  ],
};

// --- multiFamilyInfo (multi-family) ----------------------------------------
const MULTI_FAMILY_INFO = {
  id: "multiFamilyInfo",
  title: "Property Information",
  intro: "A few details about the units and the owner's costs.",
  side: "seller",
  fields: [
    short("numberOfUnits", "Number of Units", { group: "Unit Information", required: true }),
    long("rentPerUnit", "Rent for Each Unit", { group: "Unit Information", required: true }),
    long("bedsBathsPerUnit", "Number of Bedrooms & Bathrooms in Each Unit", { group: "Unit Information", required: true }),
    multi("ownerExpenses", "Owner's Expenses (Check All That Apply)", [
      "Electric", "Gas/Fuel", "Trash", "Water/Sewer", "Internet/Cable",
    ], { group: "Owner's Income/Expenses", required: true }),
  ],
};

// --- sellerLetter -----------------------------------------------------------
const SELLER_LETTER = {
  id: "sellerLetter",
  title: "Seller Letter",
  intro:
    "Share what you've loved about your home: memories, details, the neighborhood. We handle the formatting.",
  side: "seller",
  fields: [
    video("sellerLetterVideo", "The Power and Importance of a Seller Letter"),
    long("sellerLetter", "Type Your Seller Letter Here"),
    file("sellerLetterFile", "…or Upload a File Instead"),
  ],
};

// --- informationTransfer ----------------------------------------------------
const INFORMATION_TRANSFER = {
  id: "informationTransfer",
  title: "Information Transfer",
  intro:
    "Please complete before closing. We pass this to the buyers. Leave all keys/openers on the kitchen counter; the buyer's agent gets the key from the lockbox.",
  side: "seller",
  fields: [
    short("propertyAddress", "Property Address", { required: true }),
    long("sellersNames", "Sellers' Names"),
    long("forwardingAddress", "Sellers' Forwarding Address"),
    multi("keysLeft", "Keys & Access Left on Counter (Check All Left)", [
      "Keys for all doors", "Garage door openers",
    ]),
    short("garageCode", "Garage Code"),
    short("doorKeypadCode", "Door Keypad Code"),
    multi("utilitiesToChange", "Utilities to Call & Change (Check All That Apply)", [
      "Gas", "Electric", "Phone", "Cable", "Security", "Internet", "Sanitation", "Water",
    ]),
    short("otherEntryInfo", "Any Other Entry Information to Share?"),
    short("trashDay", "Trash Pick-Up Day"),
    short("trashCompany", "Trash Company"),
    short("recyclingDay", "Recycling Day"),
    short("recyclingCompany", "Recycling Company"),
    multi("miscPaperwork", "Miscellaneous Paperwork You Can Leave (Check All You Have)", [
      "Appliance informational booklets",
      "Warranties for past work (e.g. roofing)",
      "Invoices for recent work",
    ]),
  ],
};

// --- weNeedYourHelp ---------------------------------------------------------
const WE_NEED_YOUR_HELP = {
  id: "weNeedYourHelp",
  title: "We Need Your Help",
  intro:
    "Please refer a friend, family member, or coworker looking to buy, sell, or invest. We'll reach out and take great care of them.",
  side: "both",
  fields: [
    short("yourName", "Your Name", { required: true }),
    short("referralName", "Referral's Name"),
    short("referralPhone", "Referral's Phone Number"),
    short("referralEmail", "Referral's Email"),
    short("referralAddress", "Referral's Address (City, State, Zip)"),
    short("relationship", "Your Relationship to Them"),
  ],
};

// --- videoLibrary (read-only) ----------------------------------------------
const VIDEO_LIBRARY = {
  id: "videoLibrary",
  title: "Video Library",
  intro: "Short clips to help you through the process. Real links coming soon.",
  side: "both",
  fields: [
    video("video1", "Benefits of Staging: Before & After Example"),
    video("video2", "The Power and Importance of a Seller Letter"),
    video("video3", "Why You Should Invest in Updates as a Seller"),
    video("video4", "How-To: Filling Out Your Property Disclosure"),
    video("video5", "Pre-Inspection & Radon Testing"),
    video("video6", "Return on Investment When Updating Your Home Before the Sale"),
  ],
};

// --- registry ---------------------------------------------------------------
export const PORTAL_FORMS = [
  ALL_ABOUT_YOU,
  BUYER_PREFERENCES,
  PRE_APPROVAL,
  UTILITY_SHEET,
  LIST_OF_UPDATES,
  HOME_QUESTIONNAIRE,
  SELLER_LETTER,
  INFORMATION_TRANSFER,
  WE_NEED_YOUR_HELP,
  VIDEO_LIBRARY,
  ASSOCIATION_INFO,
  POOL_QUESTIONNAIRE,
  MULTI_FAMILY_INFO,
  UNIT_INFO_QUESTIONNAIRE,
];

// --- helpers ----------------------------------------------------------------
// Instance ids are "baseId:contactId" (e.g. "allAboutYou:c1"). getForm resolves
// either a plain id or an instance id back to its base form in the registry.
export function getForm(id) {
  const baseId = String(id || "").split(":")[0];
  return PORTAL_FORMS.find((f) => f.id === baseId);
}

export function formsForSide(side) {
  const s = String(side || "").trim().toLowerCase();
  if (s !== "seller" && s !== "buyer") return PORTAL_FORMS.slice();
  return PORTAL_FORMS.filter((f) => f.side === "both" || f.side === s);
}

// --- assembly (Slice 3) -----------------------------------------------------
// Expand the shared "All About You" form into one instance per client, in the
// order given. Each instance carries a stable id ("allAboutYou:{contactId}"),
// a name-titled heading, and a prefill seed from that client's contact record.
function expandAllAboutYou(clients) {
  return (Array.isArray(clients) ? clients : []).map((c) => {
    const client = c || {};
    const displayName = client.name || client.firstName || "";
    const instanceId = "allAboutYou:" + client.contactId;
    return {
      ...ALL_ABOUT_YOU,
      id: instanceId,
      title: "All About You: " + displayName,
      prefill: {
        fullLegalName: client.fullLegalName || client.name || "",
        email: client.email || "",
        mobilePhone: client.mobilePhone || "",
        currentMailingAddress: client.currentMailingAddress || "",
      },
    };
  });
}

// The listing "Form Type" options (label + value). Shown on the standalone
// Listing Request form AND the listing-lead file-request pickers; the chosen
// value is stored as deal.formType and drives formsForRequest below. One source
// of truth so the pickers and the portal assembly can never drift.
export const LISTING_FORM_TYPES = [
  { label: "Seller", value: "seller" },
  { label: "Selling and Buying", value: "selling_buying" },
  { label: "Seller Condo/Townhome", value: "seller_condo" },
  { label: "Seller (Pool)", value: "seller_pool" },
  { label: "Seller (Multi-Family)", value: "seller_multifamily" },
];

// Given a file request (formType + side + clients), return the ordered list of
// form instances the portal should assemble. Pure — no IO. "All About You" is
// expanded one-per-client; all other forms are single-instance (base id, no
// prefill).
export function formsForRequest({ formType, side, clients } = {}) {
  const type = String(formType || "").trim().toLowerCase();
  const s = String(side || "").trim().toLowerCase();
  const aboutYou = expandAllAboutYou(clients);

  // Buyer request → buyer base set.
  if (s === "buyer" || type === "buyer") {
    return [
      ...aboutYou,
      BUYER_PREFERENCES,
      PRE_APPROVAL,
      WE_NEED_YOUR_HELP,
      // VIDEO_LIBRARY, — hidden until real video links are ready (re-add to restore)
    ];
  }

  // Seller base set, with the property-type section (if any) inserted right
  // after the All About You forms. Condo swaps the Home Questionnaire for the
  // Unit Information Questionnaire.
  const extras = [];
  let questionnaire = HOME_QUESTIONNAIRE;
  if (type === "seller_condo") {
    extras.push(ASSOCIATION_INFO);
    questionnaire = UNIT_INFO_QUESTIONNAIRE;
  } else if (type === "seller_pool") {
    extras.push(POOL_QUESTIONNAIRE);
  } else if (type === "seller_multifamily") {
    extras.push(MULTI_FAMILY_INFO);
  }

  // selling_buying → seller set plus the buyer-only forms.
  const buyerAddOn = type === "selling_buying" ? [BUYER_PREFERENCES, PRE_APPROVAL] : [];

  return [
    ...aboutYou,
    ...extras,
    UTILITY_SHEET,
    LIST_OF_UPDATES,
    questionnaire,
    SELLER_LETTER,
    INFORMATION_TRANSFER,
    ...buyerAddOn,
    WE_NEED_YOUR_HELP,
    // VIDEO_LIBRARY, — hidden until real video links are ready (re-add to restore)
  ];
}

// --- form overrides (admin-configurable) ------------------------------------
// A FormOverride lets the Team Lead retitle a form, tweak individual fields
// (label / required / options / hidden), append extra fields, and reorder. It
// is applied purely on top of a base form — the input form is never mutated.
//
// FormOverride = {
//   title?: string,
//   fields?: { [fieldKey]: { label?, required?, options?, hidden? } },
//   added?: [ { key, label, type, options?, group? } ],
//   order?: [fieldKey],
// }
function overrideHasChange(ov) {
  if (!ov) return false;
  return (
    ov.title != null ||
    (ov.fields && Object.keys(ov.fields).length > 0) ||
    (Array.isArray(ov.added) && ov.added.length > 0) ||
    (Array.isArray(ov.order) && ov.order.length > 0)
  );
}

// Apply an override to a single form, returning a NEW form. Returns the input
// form unchanged when the override is missing or empty. Pure — never mutates.
export function applyOverrides(form, ov) {
  if (!form || !overrideHasChange(ov)) return form;

  const fieldOv = ov.fields || {};

  // 1. per-field label / required / options, dropping hidden fields.
  let fields = [];
  for (const f of form.fields) {
    const o = fieldOv[f.key];
    if (o && o.hidden) continue;
    if (!o) {
      fields.push(f);
      continue;
    }
    const nf = { ...f };
    if (o.label != null) nf.label = o.label;
    if (o.required != null) nf.required = o.required;
    if (o.options != null) nf.options = o.options;
    fields.push(nf);
  }

  // 2. append added fields, deduped by key (existing base keys win).
  const seen = new Set(fields.map((f) => f.key));
  if (Array.isArray(ov.added)) {
    for (const a of ov.added) {
      if (!a || !a.key || seen.has(a.key)) continue;
      seen.add(a.key);
      const nf = { key: a.key, label: a.label, type: a.type };
      if (a.options != null) nf.options = a.options;
      if (a.group != null) nf.group = a.group;
      if (a.showIf != null) nf.showIf = a.showIf;
      if (a.addLabel != null) nf.addLabel = a.addLabel;
      fields.push(nf);
    }
  }

  // 3. reorder by `order`; unlisted keys keep their relative order at the end.
  if (Array.isArray(ov.order) && ov.order.length) {
    const idx = new Map(ov.order.map((k, i) => [k, i]));
    fields = fields
      .map((f, i) => ({ f, i }))
      .sort((a, b) => {
        const ai = idx.has(a.f.key) ? idx.get(a.f.key) : Infinity;
        const bi = idx.has(b.f.key) ? idx.get(b.f.key) : Infinity;
        return ai !== bi ? ai - bi : a.i - b.i;
      })
      .map((x) => x.f);
  }

  const out = { ...form, fields };
  if (ov.title != null) out.title = ov.title;
  return out;
}

// Map each form through applyOverrides, resolving the override by the form's
// BASE id (stripping any ":contactId" instance suffix) so per-client instances
// of a shared form (e.g. "allAboutYou:c1") share one override entry.
export function applyOverridesAll(forms, overridesMap) {
  const map = overridesMap || {};
  return (Array.isArray(forms) ? forms : []).map((form) => {
    const baseId = String((form && form.id) || "").split(":")[0];
    return applyOverrides(form, map[baseId]);
  });
}

// A field with `showIf` is only shown / required / counted when its sibling
// answer equals the given value. Fields without `showIf` are always visible.
export function isFieldVisible(field, answers) {
  if (!field || !field.showIf) return true;
  const a = answers || {};
  return a[field.showIf.field] === field.showIf.equals;
}

export function groupsOf(form) {
  const out = [];
  for (const f of form.fields) {
    const g = f.group || DEFAULT_GROUP;
    if (!out.includes(g)) out.push(g);
  }
  return out;
}

export function blankAnswers(form) {
  const out = {};
  for (const f of form.fields) {
    if (f.type === "video") continue; // read-only, no answer
    if (f.type === "multi" || f.type === "repeatable") out[f.key] = [];
    else if (f.type === "file") out[f.key] = null;
    else out[f.key] = ""; // shortText | longText | single
  }
  return out;
}
